import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const MAX_REASON_LENGTH = 200;

export function ReportModal({ isOpen, onClose, onSubmit, postId }) {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason.trim()) {
      toast.error('Por favor, descreva o motivo da denúncia');
      return;
    }

    if (reason.length > MAX_REASON_LENGTH) {
      toast.error(`O motivo não pode ter mais de ${MAX_REASON_LENGTH} caracteres`);
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(postId, reason.trim());
      toast.success('Denúncia enviada com sucesso');
      setReason('');
      onClose();
    } catch (error) {
      console.error('Failed to report post:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar denúncia');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setReason('');
      onClose();
    }
  };

  const remainingChars = MAX_REASON_LENGTH - reason.length;
  const isOverLimit = remainingChars < 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent data-testid="report-modal" aria-describedby="report-description">
        <DialogHeader>
          <DialogTitle>Denunciar Post</DialogTitle>
          <DialogDescription id="report-description">
            Por favor, descreva o motivo da denúncia. Nossa equipe analisará o conteúdo.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="report-reason">Motivo da denúncia</Label>
              <Textarea
                id="report-reason"
                data-testid="report-reason-textarea"
                placeholder="Descreva o que há de errado com este post..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={`mt-1 min-h-[100px] resize-none ${isOverLimit ? 'border-red-500' : ''}`}
                disabled={isSubmitting}
                aria-label="Motivo da denúncia"
                aria-describedby="reason-counter"
                autoFocus
              />
              <div
                id="reason-counter"
                className={`text-sm text-right mt-1 ${
                  isOverLimit
                    ? 'text-red-500'
                    : remainingChars < 20
                    ? 'text-yellow-600'
                    : 'text-muted-foreground'
                }`}
              >
                {remainingChars} caracteres restantes
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              data-testid="report-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!reason.trim() || isOverLimit || isSubmitting}
              data-testid="report-submit-button"
              variant="destructive"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar Denúncia'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
